<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, watch } from 'vue';
  import { normalizeDateFrom } from '@/entities/searchCriteria';
  import { Field, FieldSize, type SearchFormGoal } from '@/entities/searchForm';
  import { type DateRange } from '@/shared/model';
  import DateFromDropdown from './DateFromDropdown.vue';
  import { DATE_FROM_TEXTS } from '../config/texts';
  import { getFlexibleClickGoal } from '../lib/flexibleClickGoal';
  import { useDateFrom } from '../model/useDateFrom';

  interface DateFromFieldProps {
    modelValue?: DateRange | null;
    size?: `${FieldSize}`;
    loading?: boolean;
    disabled?: boolean;
    fieldLabel?: string;
    dropdownTarget?: HTMLElement | null;
  }

  const props = withDefaults(defineProps<DateFromFieldProps>(), {
    modelValue: null,
    size: FieldSize.M,
    loading: false,
    disabled: false,
    fieldLabel: DATE_FROM_TEXTS.fieldLabel,
    dropdownTarget: null,
  });

  interface DateFromFieldEmits {
    (e: 'update:modelValue', value: DateRange): void;
    (e: 'manual'): void;
    (e: 'track', goal: SearchFormGoal): void;
    (e: 'open-change', isOpened: boolean): void;
  }

  const emit = defineEmits<DateFromFieldEmits>();

  const model = computed<DateRange>({
    get: () => normalizeDateFrom(props.modelValue),
    set: (value) => emit('update:modelValue', value),
  });

  const core = useDateFrom({ model });

  const onToggle = (): void => {
    core.toggle();
  };

  const onDateClick = (data: string) => {
    core.setAnchorFromISO(data);
    emit('manual');
  };

  const onSelectFlexible = (data: number) => {
    const goal = getFlexibleClickGoal(data, model.value.daysFlexible);
    core.setFlexible(data);
    emit('manual');
    if (goal) {
      emit('track', goal);
    }
  };

  watch(core.controlActivated, (isOpened) => emit('open-change', isOpened));

  defineExpose({
    validate: (): boolean => core.validate(),
    close: (): void => core.close(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="date-from-field"
    data-testid="date-from-field"
  >
    <Field
      :label="fieldLabel"
      :active="core.controlActivated.value"
      :loading="loading"
      :disabled="disabled"
      :size="size"
      value-testid="date-from-value"
      @click="onToggle"
    >
      <template #value>
        <ClientOnly>
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ core.valueLabel.value }}
          </Typo>
        </ClientOnly>
      </template>
    </Field>

    <DateFromDropdown
      :anchor-iso="core.anchorIso.value"
      :flexible-for-calendar="core.flexibleForCalendar.value"
      :flexible="core.flexible.value"
      :min-date="core.minDate.value"
      :max-date="core.maxDate.value"
      :visible="core.controlActivated.value"
      :dropdown-target="dropdownTarget"
      @date-click="onDateClick"
      @select-flexible="onSelectFlexible"
      @close="core.close"
    />
  </div>
</template>

<style scoped lang="scss">
  .date-from-field {
    position: relative;
    width: 100%;
  }
</style>
